import React, { useMemo, useState } from "react";
import styled from "styled-components";
import * as Yup from "yup";
import { Theme } from "@literati/public-ui-shared-react";
import LegacyThemeProvider from "components/Layout/DefaultThemeProvider";
import StepLayout, {
  StepLayoutClassName,
  StepLayoutProps,
} from "components/Step/StepLayout";
import GuidedSignUpCheckboxGroup, {
  GuidedSignUpMultiSelectVariant,
} from "components/GuidedSignUp/GuidedSignUpCheckboxGroup";
import {
  GoalsKey as Key,
  GoalsSubmit,
  GoalsValues,
  GoalsValuesPartial,
} from "components/Step/stepTypes";
import {
  MEMBERSHIP_GOAL_OPTIONS,
  MEMBERSHIP_GOAL_PRESET_VALUES,
  MEMBERSHIP_GOALS_HEADING,
} from "components/Membership/membershipGoalsConfig";

const MEMBERSHIP_GOAL_OTHER_VALUE = "other";

export const VALIDATION_SCHEMA = Yup.object().shape({
  [Key.SELECTIONS]: Yup.array()
    .of(Yup.string().required())
    .min(1, "Please choose at least one."),
});

export const INITIAL_VALUES: GoalsValuesPartial = {
  [Key.KIND]: "goals",
  [Key.SELECTIONS]: [],
  [Key.OTHER_GOAL_TEXT]: "",
};

function normalizeMembershipGoalsFormInitialValues(
  values: GoalsValuesPartial
): GoalsValuesPartial {
  const raw = (values[Key.SELECTIONS] ?? []).filter(
    (v): v is string => typeof v === "string"
  );
  const presetOnly = raw.filter((v) => MEMBERSHIP_GOAL_PRESET_VALUES.has(v));
  const nonPreset = raw.filter((v) => !MEMBERSHIP_GOAL_PRESET_VALUES.has(v));
  const otherText = (values[Key.OTHER_GOAL_TEXT] ?? nonPreset[0] ?? "")
    .toString()
    .slice(0, 70);

  let selections = presetOnly;
  if (
    otherText.trim().length > 0 &&
    !selections.includes(MEMBERSHIP_GOAL_OTHER_VALUE)
  ) {
    selections = [...selections, MEMBERSHIP_GOAL_OTHER_VALUE];
  }

  return {
    ...INITIAL_VALUES,
    ...values,
    [Key.KIND]: "goals",
    [Key.SELECTIONS]: selections,
    [Key.OTHER_GOAL_TEXT]: otherText,
  };
}

const StyledCheckboxGroup = styled(GuidedSignUpCheckboxGroup)`
  margin: 1.5rem 0;
`;

const StyledDiv = styled.div`
  width: 100%;
`;

export type StepLayoutPropsGoals = Omit<
  StepLayoutProps<GoalsValues>,
  "asForm" | "children" | "formId"
>;

export interface StepGoalsProps extends StepLayoutPropsGoals {
  initialValues?: GoalsValuesPartial;
  onSubmit: GoalsSubmit;
}

export const StepGoals = ({
  initialValues: initialValuesProp,
  onSubmit,
  ...stepLayoutProps
}: StepGoalsProps) => {
  const [initialValues] = useState<GoalsValuesPartial>(() =>
    normalizeMembershipGoalsFormInitialValues({
      ...INITIAL_VALUES,
      ...initialValuesProp,
    })
  );

  const checkboxOptions = useMemo(
    () =>
      MEMBERSHIP_GOAL_OPTIONS.map((opt) => ({
        id: opt.id,
        label: opt.label,
        value: opt.value,
        ...(opt.kind ? { kind: opt.kind } : {}),
      })),
    []
  );

  const handleSubmit: GoalsSubmit = (values, formikHelpers) => {
    const trimmedOther = values[Key.OTHER_GOAL_TEXT].trim();
    const baseSelections = values[Key.SELECTIONS];
    const mergedSelections =
      trimmedOther.length > 0 &&
      baseSelections.includes(MEMBERSHIP_GOAL_OTHER_VALUE)
        ? [...baseSelections, trimmedOther]
        : [...baseSelections];

    onSubmit(
      {
        ...values,
        [Key.SELECTIONS]: mergedSelections,
      },
      formikHelpers
    );
  };

  return (
    <Theme>
      <StepLayout<GoalsValues>
        asForm
        bgColor="white"
        css={`
          .${StepLayoutClassName.CONTENT_SPACING} {
            width: 100vw;
          }
          .${StepLayoutClassName.FORM} {
            max-width: 440px;
            padding: 2rem 1rem;
          }
          .${StepLayoutClassName.HEADING} {
            font-size: 1.25rem;
          }
        `}
        disableContentSpacing
        formId="membership-step-goals"
        formikProps={{
          initialValues: initialValues as GoalsValues,
          onSubmit: handleSubmit,
          validationSchema: VALIDATION_SCHEMA,
        }}
        heading={MEMBERSHIP_GOALS_HEADING}
        {...stepLayoutProps}
      >
        <LegacyThemeProvider>
          <StyledDiv>
            <StyledCheckboxGroup
              name={Key.SELECTIONS}
              otherGoalTextFieldName={Key.OTHER_GOAL_TEXT}
              options={checkboxOptions}
              variant={GuidedSignUpMultiSelectVariant.default}
            />
          </StyledDiv>
        </LegacyThemeProvider>
      </StepLayout>
    </Theme>
  );
};

export default StepGoals;
